import { lazy, Suspense, useEffect } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import * as Sentry from "@sentry/react";
import { PostHogProvider } from "posthog-js/react";

import { Fallback } from "@/components/Fallback";
import { GlobalSnackbar } from "@/components/GlobalSnackbar";
import { initializeActivityTracking } from "@/lib/auth";
import { initializePosthog, posthog } from "@/lib/posthog";
import theme from "@/lib/theme";
import { initializeVersionCheck } from "@/lib/versionCheck";
import { useAuthStore } from "@/stores/authStore";
import { useSnackbarStore } from "@/stores/snackbarStore";

import "@/lib/sentry";

const Routes = lazy(() => import("@/Routes").then((module) => ({ default: module.Routes })));

const AppComponent = () => {
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const setAuthLoading = useAuthStore((state) => state.setLoading);

  useEffect(() => {
    initializePosthog();
    checkAuth();
    initializeActivityTracking();
    setAuthLoading(false);

    const cleanupVersionCheck = initializeVersionCheck({
      showInfo: useSnackbarStore.getState().showInfo,
      removeMessages: useSnackbarStore.getState().removeMessages,
    });
    return cleanupVersionCheck;
  }, [checkAuth, setAuthLoading]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <PostHogProvider client={posthog}>
        <Suspense fallback={<></>}>
          <Routes />
        </Suspense>
        <GlobalSnackbar />
      </PostHogProvider>
    </ThemeProvider>
  );
};

const App = Sentry.withErrorBoundary(AppComponent, {
  fallback: Fallback,
});

export default App;
